import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { getApiError } from '../api/client';
import { useTenantStore } from '../store/tenantStore';
import { useCompanyPath, useCompanySlug } from '../utils/routing';
import Logo from '../components/Logo';

export default function LocationSelectPage() {
  const navigate = useNavigate();
  const slug = useCompanySlug();
  const modulesPath = useCompanyPath('/modules');
  const { branches, setBranches, setBranch, branchId } = useTenantStore();
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get('/admin/rbac/my-permissions')
      .then(r => {
        const roles: string[] = r.data.roles || [];
        setIsAdmin(r.data.is_superuser || roles.includes('admin') || roles.includes('super_admin'));
      })
      .catch(() => {});
    api.get('/tenants/branches/list')
      .then(r => {
        const list = r.data || [];
        setBranches(list);
        if (list.length === 1) {
          selectLocation(list[0]);
        } else if (branchId && list.some((b: { id: number }) => b.id === branchId)) {
          navigate(modulesPath, { replace: true });
        }
      })
      .catch(e => toast.error(getApiError(e, 'Could not load locations')))
      .finally(() => setLoading(false));
  }, []);

  const selectLocation = async (branch: { id: number; name: string; city?: string }) => {
    setSelecting(branch.id);
    try {
      await api.post('/tenants/branches/select', { branch_id: branch.id });
      setBranch(branch.id, branch.name);
      toast.success(`Location set to ${branch.name}`);
      navigate(modulesPath, { replace: true });
    } catch (e) {
      toast.error(getApiError(e, 'Could not select location'));
    } finally {
      setSelecting(null);
    }
  };

  const selectAllBranches = () => {
    setBranch(null, 'All Branches');
    toast.success('Viewing data across all branches');
    navigate(modulesPath, { replace: true });
  };

  return (
    <div className="page-shell flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <Logo size="md" />
          <h1 className="page-title mt-6">Select your location</h1>
          <p className="page-subtitle mt-2">
            Choose the branch you are working from. Master data is shared company-wide; transactions and stock are filtered by branch.
          </p>
        </div>

        {loading ? (
          <div className="card p-8 text-center text-content-muted">Loading locations…</div>
        ) : branches.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-content-secondary mb-4">No locations configured yet.</p>
            <button onClick={() => navigate(modulesPath)} className="btn-primary">Continue anyway</button>
          </div>
        ) : (
          <div className="space-y-3">
            {isAdmin && branches.length > 1 && (
              <button
                onClick={selectAllBranches}
                disabled={selecting !== null}
                className="card w-full p-5 text-left card-hover flex items-center gap-4 disabled:opacity-60 border-2 border-primary-200 bg-primary-50/50"
              >
                <div className="w-12 h-12 rounded-xl gradient-bg text-white flex items-center justify-center text-xl">🌐</div>
                <div className="flex-1">
                  <div className="font-semibold text-content">All Branches</div>
                  <div className="text-sm text-content-secondary">
                    View data across all {branches.length} locations
                    <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">Admin</span>
                  </div>
                </div>
                <span className="text-content-muted">→</span>
              </button>
            )}
            {branches.map(b => (
              <button
                key={b.id}
                onClick={() => selectLocation(b)}
                disabled={selecting !== null}
                className="card w-full p-5 text-left card-hover flex items-center gap-4 disabled:opacity-60"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center text-xl">
                  {b.is_head_office ? '🏢' : '📍'}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-content">{b.name}</div>
                  <div className="text-sm text-content-secondary">
                    {[b.city, b.state].filter(Boolean).join(', ') || b.code || 'Branch'}
                    {b.is_head_office && <span className="ml-2 text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">Head Office</span>}
                  </div>
                </div>
                {selecting === b.id ? (
                  <span className="w-5 h-5 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                ) : (
                  <span className="text-content-muted">→</span>
                )}
              </button>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-content-muted mt-6">
          Company: <strong>{slug}</strong> · You can change location later from the top bar
        </p>
      </div>
    </div>
  );
}
